import React, { useCallback, useEffect, useRef, useState } from "react";
import { BsFilter } from "react-icons/bs";
import { FiDownload } from "react-icons/fi";
import { MdOutlineDelete } from "react-icons/md";
import { useSearchParams } from "react-router-dom";
import Button from "../../../components/Button/Button";
import FilterApplied from "../../../components/FilterApplied/FilterApplied";
import Input from "../../../components/Input/Input";
import ModalComponent from "../../../components/Modal/ModalComponent";
import Select from "../../../components/Select/Select";
import SelectLicences from "../../../components/SelectLicences/SelectLicences";
import { useListManager } from "../../../contexts/list.manager";
import "./FiltersEvents.css";
import Container from "../../../components/Container/Container";
import SearchInput from "../../../components/SearchInput/SearchInput";
import { debounce } from "../../../utils/timer";
import { EVENTS, getEventData, getEventTypeTranslationKey } from "../../../constants/events";
import ReactSelect from "react-select";
import Checkbox from "../../../components/Checkbox/Checkbox";
import ToggleButton from "../../../components/ToggleButton/ToggleButton";
import { createDateFromFormat, formatDate } from "../../../utils/date";
import { useTranslation } from "react-i18next";
import { LuFilter } from "react-icons/lu";

/** Keys dos filtros que são strings simples */
const FILTER_KEYS = [
  "search", "name", "eventType", "startDate", "endDate",
  "startTime", "endTime", "licence", "detail", "data",
  "user", "uuid", "result", "info", "carName", "carBrand",
  "carPlate", "carColor",
];

/**
 * Constrói um objeto URLSearchParams apenas com os filtros que possuem valor,
 * mantendo a URL limpa quando não há filtros ativos.
 */
function buildSearchParams(filters) {
  const params = new URLSearchParams();
  for (const key of FILTER_KEYS) {
    const value = filters[key];
    if (value != null && value !== "") {
      params.set(key, value);
    }
  }
  if (filters.continuousInterval) {
    params.set("continuousInterval", "1");
  }
  return params;
}

const FiltersEvents = (props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initializedRef = useRef(false);

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [search, setSearch] = useState(null);
  const [name, setName] = useState("");
  const [eventType, setEventType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [continuousInterval, setContinuousInterval] = useState(false);
  const [licence, setLicence] = useState("");
  const [detail, setDetail] = useState("");
  const [data, setData] = useState("");
  const [user, setUser] = useState("");
  const [uuid, setUuid] = useState("");
  const [result, setResult] = useState("");
  const [info, setInfo] = useState("");
  const [carName, setCarName] = useState("");
  const [carBrand, setCarBrand] = useState("");
  const [carPlate, setCarPlate] = useState("");
  const [carColor, setCarColor] = useState("");
  const [showFiltersApplied, setShowFiltersApplied] = useState(false);
  const { closeForm } = useListManager();
  const { t } = useTranslation();

  // ── Inicialização a partir da URL ──────────────────────────────────
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const sp = searchParams;
    const hasAny = FILTER_KEYS.some((k) => sp.has(k)) || sp.has("continuousInterval");
    if (!hasAny) return;

    const urlSearch = sp.get("search") || "";
    const urlName = sp.get("name") || "";
    const urlEventType = sp.get("eventType") || "";
    const urlStartDate = sp.get("startDate") || "";
    const urlEndDate = sp.get("endDate") || "";
    const urlStartTime = sp.get("startTime") || "";
    const urlEndTime = sp.get("endTime") || "";
    const urlContinuous = sp.get("continuousInterval") === "1";
    const urlLicence = sp.get("licence") || "";
    const urlDetail = sp.get("detail") || "";
    const urlData = sp.get("data") || "";
    const urlUser = sp.get("user") || "";
    const urlUuid = sp.get("uuid") || "";
    const urlResult = sp.get("result") || "";
    const urlInfo = sp.get("info") || "";
    const urlCarName = sp.get("carName") || "";
    const urlCarBrand = sp.get("carBrand") || "";
    const urlCarPlate = sp.get("carPlate") || "";
    const urlCarColor = sp.get("carColor") || "";

    setSearch(urlSearch);
    setName(urlName);
    setEventType(urlEventType);
    setStartDate(urlStartDate);
    setEndDate(urlEndDate);
    setStartTime(urlStartTime);
    setEndTime(urlEndTime);
    setContinuousInterval(urlContinuous);
    setLicence(urlLicence);
    setDetail(urlDetail);
    setData(urlData);
    setUser(urlUser);
    setUuid(urlUuid);
    setResult(urlResult);
    setInfo(urlInfo);
    setCarName(urlCarName);
    setCarBrand(urlCarBrand);
    setCarPlate(urlCarPlate);
    setCarColor(urlCarColor);
    setShowFiltersApplied(true);

    props.filtersApplied({
      search: urlSearch,
      name: urlName,
      eventType: urlEventType,
      startDate: urlStartDate,
      endDate: urlEndDate,
      startTime: urlStartTime,
      endTime: urlEndTime,
      continuousInterval: urlContinuous,
      licence: urlLicence,
      detail: urlDetail,
      data: urlData,
      user: urlUser,
      uuid: urlUuid,
      result: urlResult,
      info: urlInfo,
      carName: urlCarName,
      carBrand: urlCarBrand,
      carPlate: urlCarPlate,
      carColor: urlCarColor,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helper: sincroniza filtros → URL ───────────────────────────────
  const syncFiltersToUrl = useCallback((filters) => {
    setSearchParams(buildSearchParams(filters), { replace: true });
  }, [setSearchParams]);

  const clearFilters = () => {
    setSearch("");
    setName("");
    setEventType("");
    setStartDate("");
    setEndDate("");
    setStartTime("");
    setEndTime("");
    setContinuousInterval(false);
    setLicence("");
    setDetail("");
    setData("");
    setUser("");
    setUuid("");
    setResult("");
    setInfo("");
    setCarName("");
    setCarBrand("");
    setCarColor("");
    setCarPlate("");
    setShowFiltersApplied(false);
    closeForm();

    const emptyFilters = {
      search: "",
      name: "",
      eventType: "",
      startDate: "",
      endDate: "",
      startTime: "",
      endTime: "",
      continuousInterval: false,
      licence: "",
      detail: "",
      data: "",
      user: "",
      uuid: "",
      result: "",
      info: "",
      carName: "",
      carBrand: "",
      carColor: "",
      carPlate: "",
    };
    props.filtersApplied(emptyFilters);
    syncFiltersToUrl(emptyFilters);
  };

  const handleConfirm = (searchChanged) => {
    setShowFiltersApplied(true);
    setModalIsOpen(false);
    closeForm();

    const filters = {
      search: searchChanged,
      name: name,
      eventType: eventType,
      startDate: startDate,
      endDate: endDate,
      startTime: startTime,
      endTime: endTime,
      continuousInterval: continuousInterval,
      licence: licence.SN_equipment || "",
      detail: detail,
      data: data,
      user: user,
      uuid: uuid,
      result: result,
      info: info,
      carName: carName,
      carBrand: carBrand,
      carPlate: carPlate,
      carColor: carColor,
    };
    props.filtersApplied(filters);
    syncFiltersToUrl(filters);
  };

  const handleCancel = () => {
    setModalIsOpen(false);
  };

  const changeSearchDebounce = useCallback(debounce((search) => handleConfirm(search), 700), [
    name,
    eventType,
    startDate,
    endDate,
    startTime,
    endTime,
    continuousInterval,
    licence,
    detail,
    data,
    user,
    uuid,
    result,
    info,
    carName,
    carBrand,
    carColor,
    carPlate,
  ]);

  useEffect(() => {
    if (search == null) return;
    changeSearchDebounce(search);
  }, [search]);

  function removeFilter(filter) {
    if (filter === 'search') setSearch("");
    if (filter === 'eventType') setEventType("");
    if (filter === 'name') setName("");
    if (filter === 'startDate') setStartDate("");
    if (filter === 'endDate') setEndDate("");
    if (filter === 'startTime') setStartTime("");
    if (filter === 'endTime') setEndTime("");
    if (filter === 'continuousInterval') setContinuousInterval(false);
    if (filter === 'licence') setLicence("");
    if (filter === 'detail') setDetail("");
    if (filter === 'data') setData("");
    if (filter === 'user') setUser("");
    if (filter === 'uuid') setUuid("");
    if (filter === 'result') setResult("");
    if (filter === 'info') setInfo("");
    if (filter === 'carName') setCarName("");
    if (filter === 'carBrand') setCarBrand("");
    if (filter === 'carColor') setCarColor("");
    if (filter === 'carPlate') setCarPlate("");

    const updatedFilters = {
      search: filter === "search" ? "" : search,
      name: filter === "name" ? "" : name,
      eventType: filter === "eventType" ? "" : eventType,
      startDate: filter === "startDate" ? "" : startDate,
      endDate: filter === "endDate" ? "" : endDate,
      startTime: filter === "startTime" ? "" : startTime,
      endTime: filter === "endTime" ? "" : endTime,
      continuousInterval: filter === "continuousInterval" ? false : continuousInterval,
      licence: filter === "licence" ? "" : licence.SN_equipment || "",
      detail: filter === "detail" ? "" : detail,
      data: filter === "data" ? "" : data,
      user: filter === "user" ? "" : user,
      uuid: filter === "uuid" ? "" : uuid,
      result: filter === "result" ? "" : result,
      info: filter === "info" ? "" : info,
      carName: filter === "carName" ? "" : carName,
      carBrand: filter === "carBrand" ? "" : carBrand,
      carPlate: filter === "carPlate" ? "" : carPlate,
      carColor: filter === "carColor" ? "" : carColor,
    };
    props.filtersApplied(updatedFilters);
    syncFiltersToUrl(updatedFilters);
  }

  return (
    <Container className="pt-0!">
      <div className="events-filters-buttons-container px-0! mx-0! mt-0! justify-between!">
        <SearchInput
          value={search}
          placeholder={t("common.search")}
          onChange={({ target }) => setSearch(target.value)}
        />
        <div className="flex flex-row-reverse items-center gap-2.5">
          <Button
            className="button-blue events-filters-button"
            label={t("common.advancedFilters")}
            icon={<LuFilter size={18} />}
            onClick={() => setModalIsOpen(true)}
          />
          <Button
            className="button-white events-filters-button"
            label={t("common.clearFilters")}
            icon={<MdOutlineDelete size={17} />}
            onClick={() => clearFilters()}
          />
          <Button
            className="button-white events-filters-button"
            label={props.isExporting
              ? `Recebendo CSV (${props.exportedBytesLabel})`
              : t("common.exportCSV")}
            icon={<FiDownload size={17} />}
            onClick={props.onDownloadCsv}
            disabled={props.isExporting}
          />
        </div>
      </div>
      {showFiltersApplied && (
        <div className={"events-filters-applied-container"}>
          <FilterApplied filter={search} removeFilter={() => removeFilter("search")} />
          <FilterApplied filter={eventType ? t(`${getEventTypeTranslationKey(eventType)}`) : null} removeFilter={() => removeFilter("eventType")} />
          <FilterApplied filter={name ? t(`${getEventData(name)?.name}`) : null} removeFilter={() => removeFilter("name")} />
          <FilterApplied filter={startDate ? formatDate(createDateFromFormat("Y-m-d", startDate)) : null} removeFilter={() => removeFilter("startDate")} />
          <FilterApplied filter={endDate ? formatDate(createDateFromFormat("Y-m-d", endDate)) : null} removeFilter={() => removeFilter("endDate")} />
          <FilterApplied filter={startTime} removeFilter={() => removeFilter("startTime")} />
          <FilterApplied filter={endTime} removeFilter={() => removeFilter("endTime")} />
          {continuousInterval && (
            <FilterApplied filter={t("common.continuousInterval")} removeFilter={() => removeFilter("continuousInterval")} />
          )}
          <FilterApplied filter={licence.SN_equipment} removeFilter={() => removeFilter("licence")} />
          <FilterApplied filter={detail} removeFilter={() => removeFilter("detail")} />
          <FilterApplied filter={data} removeFilter={() => removeFilter("data")} />
          <FilterApplied filter={user} removeFilter={() => removeFilter("user")} />
          <FilterApplied filter={uuid} removeFilter={() => removeFilter("uuid")} />
          <FilterApplied filter={result} removeFilter={() => removeFilter("result")} />
          <FilterApplied filter={info} removeFilter={() => removeFilter("info")} />
          <FilterApplied filter={carName} removeFilter={() => removeFilter("carName")} />
          <FilterApplied filter={carBrand} removeFilter={() => removeFilter("carBrand")} />
          <FilterApplied filter={carColor} removeFilter={() => removeFilter("carColor")} />
          <FilterApplied filter={carPlate} removeFilter={() => removeFilter("carPlate")} />
        </div>
      )}
      <ModalComponent
        modalIsOpen={modalIsOpen}
        modalText={t("common.advancedFilters")}
        handleConfirm={() => handleConfirm(search)}
        handleCancel={handleCancel}
      >
        <div className="events-filters-modal-inputs-container">
          <div className="filters-group group-licence-type mb-3!">
            <div className="input-group">
              <SelectLicences
                value={licence}
                onChange={(licence) => setLicence(licence)}
              />
            </div>
            <div className="input-group">
              <Select
                labelText={t("common.type")}
                name="type"
                id="type"
                value={eventType}
                onChange={({ target }) => setEventType(target.value)}
                options={[
                  { value: "Alarme", text: t("events.alarm") },
                  { value: "Configuracao", text: t("events.configuration") },
                  { value: "Acesso", text: t("events.access") },
                  { value: "Sistema", text: t("events.system") },
                ]}
              />
            </div>
            <div className="input-group">
              <div className="input-label">
                <span>{t("common.event")}</span>
                <ReactSelect
                  className="basic-single reception-search-user-select w-full!"
                  options={
                    EVENTS
                      .filter(e => !eventType || eventType.trim() === '' || e.type === eventType)
                      .map(e => { return { value: e.original, label: t(e.translationKey) } })
                  }
                  isSearchable
                  onChange={(e) => setName(e.value)}
                />
              </div>
            </div>
          </div>

          <div className="filters-group group-date-range mb-3.5!">
            <h3 className="filters-group-title">{t("common.timeInterval")}</h3>
            <div className="input-group">
              <Input
                value={startDate}
                type="date"
                labelText={t("common.startDate")}
                onChange={({ target }) => setStartDate(target.value)}
                className="input-form"
              />
            </div>
            <div className="input-group">
              <Input
                value={endDate}
                type="date"
                labelText={t("common.endDate")}
                onChange={({ target }) => setEndDate(target.value)}
                className="input-form"
              />
            </div>
            <div className="input-group">
              <Input
                name="start_hour"
                id="start_hour"
                type="time"
                min="00:00"
                max="23:59"
                labelText={t("common.startTime")}
                value={startTime}
                onChange={({ target }) => { setStartTime(target.value); }}
                disabled={props.type === "consulta"}
                required={false}
                tooltipText={t("common.startTime")}
                tooltipVisible={true}
              />
            </div>
            <div className="input-group">
              <Input
                name="end_hour"
                id="end_hour"
                type="time"
                min="00:00"
                max="23:59"
                labelText={t("common.endTime")}
                value={endTime}
                onChange={({ target }) => { setEndTime(target.value); }}
                disabled={props.type === "consulta"}
                required={false}
                tooltipText={t("common.endTime")}
                tooltipVisible={true}
              />
            </div>
            <div className="w-fit! mt-1!">
              <ToggleButton
                style={{ width: 'fit-content' }}
                label={t("common.continuousInterval")}
                onChange={(e) => setContinuousInterval(e.target.checked)}
                disabled={props.type === "consulta"}
                selected={continuousInterval}
              />
            </div>
          </div>

          <div className="filters-group group-general-details mb-3!">
            <h3 className="filters-group-title">{t("common.generalDetails")}</h3>
            <div className="input-group">
              <Input
                value={detail}
                labelText={t("common.details")}
                onChange={({ target }) => setDetail(target.value)}
                className="input-form"
              />
            </div>
            <div className="input-group">
              <Input
                value={data}
                labelText={t("common.data")}
                onChange={({ target }) => setData(target.value)}
                className="input-form"
              />
            </div>
            <div className="input-group">
              <Input
                value={user}
                labelText={t("common.user")}
                onChange={({ target }) => setUser(target.value)}
                className="input-form"
              />
            </div>
            <div className="input-group">
              <Input
                value={uuid}
                labelText="UUID"
                onChange={({ target }) => setUuid(target.value)}
                className="input-form"
              />
            </div>
            <div className="input-group">
              <Input
                value={result}
                labelText={t("common.result")}
                onChange={({ target }) => setResult(target.value)}
                className="input-form"
              />
            </div>
            <div className="input-group">
              <Input
                value={info}
                labelText={t("common.info")}
                onChange={({ target }) => setInfo(target.value)}
                className="input-form"
              />
            </div>
          </div>

          <div className="filters-group group-car-info">
            <h3 className="filters-group-title">{t("common.vehicle")}</h3>
            <div className="input-group">
              <Input
                value={carName}
                labelText={t("common.vehicleName")}
                onChange={({ target }) => setCarName(target.value)}
                className="input-form"
              />
            </div>
            <div className="input-group">
              <Input
                value={carBrand}
                labelText={t("common.vehicleBrand")}
                onChange={({ target }) => setCarBrand(target.value)}
                className="input-form"
              />
            </div>
            <div className="input-group">
              <Input
                value={carPlate}
                labelText={t("common.vehicleLicensePlate")}
                onChange={({ target }) => setCarPlate(target.value)}
                className="input-form"
              />
            </div>
            <div className="input-group">
              <Input
                value={carColor}
                labelText={t("common.vehicleColor")}
                onChange={({ target }) => setCarColor(target.value)}
                className="input-form"
              />
            </div>
          </div>
        </div>
      </ModalComponent>
    </Container>
  );
};

export default FiltersEvents;
